import { useMemo } from 'react';
import styled from 'styled-components';
import { fonts } from 'ui/theme';
import { splitArray } from 'utils/arrayHelpers';

import { useFiltersContext, useQueryContext } from '../contexts';
import { ChannelSubscriptionSetting } from './ChannelSubscriptionSetting';
import { InactiveChannelDivider } from './InactiveChannelDivider';
import { NoChannels } from './NoChannels';
import { NoResults } from './NoResults';
import { SubscriptionSettingsListSkeleton } from './SubscriptionSettingsListSkeleton';

const Wrapper = styled.ul`
  margin: 0;
  padding: 0;
  font-family: ${fonts.sansSerif};
`;

export const SubscriptionSettingsList = () => {
  const { clearFilters, search } = useFiltersContext();
  const { channels, isLoading } = useQueryContext();
  const sortedChannels = useMemo(
    () => channels.sort((a, b) => (a.name > b.name ? 1 : -1)),
    [channels],
  );
  const [subscribedChannels, unsubscribedChannels] = splitArray(
    sortedChannels,
    (channel) => channel.isSubscribed,
  );

  if (!isLoading && !sortedChannels.length) {
    const isUsingFilters = !!clearFilters || !!search.length;
    return <Wrapper>{isUsingFilters ? <NoResults /> : <NoChannels />}</Wrapper>;
  }

  /**
   * Keep all settings panels in the same array/React keyspace so
   * that settings panels remain mounted between state transitions and
   * list reorder animations work properly.
   */
  const settingsPanels = [
    ...subscribedChannels.map((channel) => (
      <ChannelSubscriptionSetting channel={channel} key={channel.id} />
    )),
    !!unsubscribedChannels.length && !!subscribedChannels.length && (
      <InactiveChannelDivider key="divider" />
    ),
    ...unsubscribedChannels.map((channel) => (
      <ChannelSubscriptionSetting channel={channel} key={channel.id} />
    )),
  ];

  return (
    <Wrapper data-test="subscriptions-list">
      {isLoading ? <SubscriptionSettingsListSkeleton /> : settingsPanels}
    </Wrapper>
  );
};
